// Module `activities` (API-v3 7): activities and calendar, logframe import,
// meetings and steering committee, deliverables, missions. Owned by WP activities.
import { z } from "zod";
import { ACTIVITY_TYPES, DELIVERABLE_KINDS, MEETING_KINDS, MEETING_MODES, PAPER_SOURCES, PROOF_KINDS, TODO_PHASES, type ActivityType, type DeliverableKind, type MeetingKind, type PaperSource, type ProofKind, type TodoPhase } from "../enums";
import { LIMITS } from "../limits";
import { httpUrlOrEmpty, uuid } from "../schemas/common";
import {
  centsV3,
  isoDate,
  isoMonth,
  text,
  textOrEmpty,
  textOrNull,
  type FileRef,
  type FromRecord,
  type HistoryEntry,
  type Id,
  type Link,
  type StatusChip,
  type Steps,
} from "./common";
import { questionBody, type QuestionV3 } from "./events";
import type { MailStatus } from "./mail";

// ---------- 7.1 activities and calendar ----------

/** AC3/AC-N6: how the warning starts when an activity's dates moved away from its approved ToR (the notice links "Make a new ToR version" to it). */
export const TOR_DATES_DRIFT = "The dates differ from the approved ToR";

export type ActivityRow = {
  id: Id;
  code: string;
  title: string;
  type: ActivityType;
  typeLabel: string;
  meta: string;
  startDate: string | null;
  endDate: string | null;
  dueDate: string | null;
  budgetCents: number | null;
  /** AC-F5: what is spent on it so far (approved, paid, from timesheets), for the team; null for partners. Shown as "spent / ToR". */
  spentCents?: number | null;
  status: StatusChip;
  path: string;
  /** W2: the other results it serves, in plain words ("Also serves R2"); left out when it serves one. */
  alsoServes?: string;
  /** AC3 (round 5, team only): where it moved away from its approved ToR, and event deadlines or sessions outside its dates; left out when none. */
  torDrift?: string[];
};
/** A logframe result in a list or picker. title: the full statement; shortName: a few plain words, null = none yet. */
export type ResultRefV3 = { id: Id; code: string; title: string; shortName: string | null };
/** A budget line in a picker: "3. Flights within Europe". */
/**
 * A budget line in a picker. GL5 (Marius round 5): heading = the funder's
 * heading it sits under ("Per diems"), so "Europe" and "Other destinations"
 * are readable; label = "14 · Per diems – Europe" (see budgetLineLabel).
 */
export type BudgetLineRefV3 = { id: Id; number: number; name: string; heading?: string | null; code?: string | null; label?: string };

/** Words a heading numbers on purpose ("Phase1", "lot3"): their number is not a footnote. */
const NUMBERED_WORD = /^(?:phase|lot|wp|stage|step|part|module|component|package|annex|year|round|task|activity|output|outcome|result|batch|wave|session|day|week|month|level|tier|region|zone|area|section|item|option|scenario|track|pillar|objective|priority)$/i;

/**
 * A heading or name without the footnote number the funder's template sticks
 * to its end (a superscript there): "1. Human Resources14", "Total Cost (in
 * EUR)3", "Human Resources¹⁴". Only digits glued to a lower-case word (or to a
 * closing bracket) count, so a heading's own number stays: "WP2", "Lot3",
 * "Component A1", "Phase1", "Lot 3".
 */
export function withoutFootnoteNumber(s: string): string {
  const t = s.trim();
  const superscript = t.replace(/[\u00B9\u00B2\u00B3\u2070-\u2079]+$/u, "");
  if (superscript !== t) return superscript.trim();
  const m = /(\p{L}+|\))(\d{1,2})$/u.exec(t);
  if (!m) return t;
  const glued = m[1] === ")" || (/\p{Ll}{3}$/u.test(m[1]) && !NUMBERED_WORD.test(m[1]));
  return glued ? t.slice(0, m.index + m[1].length).trim() : t;
}

/**
 * A budget heading as shown: without a footnote number stuck to its last word
 * (withoutFootnoteNumber: "1. Human Resources14" reads "1. Human Resources").
 * What is stored is not changed.
 */
export function budgetHeadingTitle(h: string | null | undefined): string {
  return isPlaceholderHeading(h) ? "" : withoutFootnoteNumber(h ?? "");
}

/**
 * MD-F4 / AU-F1 (Marius, 1 Nov 2026): a "heading" that is only a yes/no
 * value ("ja", "nee", "yes", "no") came from a column of the budget file
 * that is no heading (a "fee line?" column). It is never shown as a heading
 * ("1. ja – Fees key experts") and the import does not take it.
 */
export function isPlaceholderHeading(h: string | null | undefined): boolean {
  return /^(ja|nee|yes|no|oui|non|true|false)$/i.test((h ?? "").trim());
}

/**
 * Does the name start with the funder's code as a whole code? "1.1 Travel"
 * and "1.1. Travel" do for code "1.1"; "1.10 Travel" and "1.1.2 Travel" do not.
 */
const startsWithCode = (name: string, code: string): boolean => name.startsWith(code) && /^(\.?(\s|$)|[\s:–-]|$)/.test(name.slice(code.length));

/** The heading's words, without its number: "1. Human Resources14" -> "Human Resources". */
const headingWords = (h: string | null | undefined) => budgetHeadingTitle(h).replace(/^\d+(\.\d+)*\.?\s+/, "").trim();

/**
 * D15 (Simon S-069, Marius 1 Nov): the one separator between a budget line's
 * number (or code) and its name, everywhere: "1 · Fees key experts",
 * "5.2 · Travel". budgetLineLabel and lineNumberName both use it.
 */
export const LINE_SEP = " · ";

/**
 * GL5: a budget line with its heading, for pickers and lists: "14 · Per diems –
 * Europe". No heading, or a name that already says it: "3 · Studies". With the
 * funder's code when the name does not start with it, so two lines of the
 * same name under one heading can be told apart: "3 · Human Resources –
 * Project Coordinator (1.1.1.2)".
 */
export function budgetLineLabel(l: { number: number | null; name: string; heading?: string | null; code?: string | null }): string {
  const h = headingWords(l.heading);
  const code = l.code?.trim();
  const name = h && !l.name.toLowerCase().includes(h.toLowerCase()) ? `${h} – ${l.name}` : l.name;
  const coded = code && !startsWithCode(l.name.trim(), code) ? `${name} (${code})` : name;
  return l.number === null ? coded : `${l.number}${LINE_SEP}${coded}`;
}
/**
 * UI-BU1 B11 (Marius's Budget wireframe): the line's number before its name,
 * as the funder numbers it: its code when it has one ("5.2 · Travel"), else the
 * app's line number ("5 · Travel"). A name that already starts with its code
 * keeps it once. D15: the separator is LINE_SEP.
 */
export function lineNumberName(l: { number: number | null; name: string; code?: string | null }): string {
  const code = l.code?.trim();
  const name = l.name.trim();
  if (code) return startsWithCode(name, code) ? `${code}${LINE_SEP}${name.slice(code.length).replace(/^[\s.:–-]+/, "")}` : `${code}${LINE_SEP}${name}`;
  return l.number === null ? name : `${l.number}${LINE_SEP}${name}`;
}
/**
 * A line of work (W1): a planned bucket, "5 consultations". done = its
 * activities with status Done; total = all its activities; plannedCount = how
 * many the contract plans (null = not said). madeAtConversion: made from the
 * existing activities when lines of work arrived, for the team to rename.
 */
export type LineOfWorkV3 = {
  id: Id;
  name: string;
  /** LF17 (round 5): the code from the logframe ("A1.2"); "" = none. */
  code?: string;
  kind: ActivityType;
  kindLabel: string;
  plannedCount: number | null;
  proofNote: string;
  madeAtConversion: boolean;
  results: ResultRefV3[];
  budgetLines: BudgetLineRefV3[];
  /** W3: the proof each activity must produce (a structured list; proofNote says it in words). */
  requiredProof: ProofKind[];
  /** W3: the feedback questions an event of this line starts with (the event form's shape, with indicator links). Copied, not linked. */
  feedbackQuestions: LineQuestionV3[];
  /** W3: who owns a new activity of this line by default (null = the person creating it). */
  defaultOwner: { id: Id; name: string } | null;
  done: number;
  total: number;
};
/** A default feedback question of a line of work: the event question's fields, without a place on a form yet. */
export type LineQuestionV3 = Pick<QuestionV3, "id" | "label" | "kind" | "options" | "required" | "useForReporting"> & { indicatorId: Id | null; countsAs: string[] };
/** A default feedback question in the line of work dialog; id kept when it is an existing one. */
export const lineQuestionBody = questionBody.extend({ id: uuid.optional() });
export type LineQuestionBody = z.infer<typeof lineQuestionBody>;
export type ActivityList = {
  tiles: { planned: number; inProgress: number; waitingOnFunder: number; done: number };
  typeCounts: Record<"all" | ActivityType, number>;
  /** By logframe result (kept while reports move over to lines of work). */
  groups: { result: ResultRefV3 | null; done: number; total: number; rows: ActivityRow[] }[];
  /**
   * By line of work, in the project's order; line null = activities in no line. Staff also see lines with no activity yet.
   * budgetCents: the budgets of all its activities added up (round 4: "2 of 4 done · €93,750").
   */
  lines: { line: LineOfWorkV3 | null; done: number; total: number; budgetCents?: number; rows: ActivityRow[] }[];
  /** Round 4 action cards above the list (staff only; at most four). */
  actions?: ActivityAction[];
  /** The project's today (Brussels), for the Overview's "Coming up". */
  today?: string;
};
/** R2-012: the activities the signed-in person starred in this project (GET /starred-activities), oldest star first. */
export type ActivityStars = { activityIds: Id[] };
/** One action card: "CHANGES REQUESTED / ToR A2.2 · Training of trainers / The funder asked … / Revise the ToR →". */
export type ActivityAction = { key: string; kicker: string; title: string; detail: string; action: { label: string; path: string } };
export const lineOfWorkBody = z.object({
  name: text(LIMITS.lineOfWorkName, "Name"),
  /** LF17 (round 5): the code from the logframe ("A1.2"). Left out: none on create, unchanged on a change. */
  code: textOrEmpty(16, "Code").optional(),
  /**
   * A4e: a line holds activities of any kind; its kind is derived (the most common
   * type of its activities). Kept for older callers: on create it is the empty
   * line's starting kind (left out: event); on a change it is ignored.
   */
  kind: z.enum(ACTIVITY_TYPES).optional(),
  plannedCount: z.number().int("Enter a whole number.").min(0, "Enter 0 or more.").max(10_000, "Enter at most 10,000.").nullable(),
  resultIds: z.array(uuid).max(50),
  budgetLineIds: z.array(uuid).max(100),
  proofNote: textOrEmpty(LIMITS.lineOfWorkProof, "Proof needed"),
  /** W3: the proof each activity must produce. Left out on create: none. */
  requiredProof: z.array(z.enum(PROOF_KINDS)).max(PROOF_KINDS.length).optional(),
  /** W3: the default feedback questions (events). Left out on create: none. */
  feedbackQuestions: z.array(lineQuestionBody).max(20, "Add at most 20 questions.").optional(),
  /** W3: who owns a new activity of the line by default; someone on the project team. null = the person creating it. */
  defaultOwnerUserId: uuid.nullable().optional(),
});
export type LineOfWorkBody = z.infer<typeof lineOfWorkBody>;
export const lineOfWorkPatch = lineOfWorkBody.partial();
export type LineOfWorkPatch = z.infer<typeof lineOfWorkPatch>;
export const activitiesQuery = z.object({
  type: z.enum(["all", ...ACTIVITY_TYPES]).default("all"),
  status: z.string().max(40).optional(),
  period: z
    .string()
    // R2-018: a reporting period's id, as the report pages' Period picker has it; "whole" or "all" for the
    // whole project; the older words (year-1, next-90, rp-2) still work for links made before.
    .regex(/^(whole|all|next-90|year-\d{1,2}|rp-\d{1,2}|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i)
    .default("whole"),
  q: z.string().trim().max(LIMITS.searchQuery).optional(),
});
export type ActivityOptions = {
  results: ResultRefV3[];
  /**
   * The lines of work a new activity can go into (W1), with what a new
   * activity copies from them (W3): budget lines, proof, feedback questions
   * (events) and the default owner. done/total/plannedCount: "3 of 5 done".
   */
  lines: {
    id: Id;
    name: string;
    kind: ActivityType;
    /** A4e: every kind the line holds ("Events, deliverables and (N)KE activities"); one kind: its plural. */
    kindLabel?: string;
    resultIds: Id[];
    plannedCount?: number | null;
    done?: number;
    total?: number;
    budgetLineIds?: Id[];
    requiredProof?: ProofKind[];
    /** indicator: "Counts towards R1.2 …" in plain words, null = none. */
    feedbackQuestions?: { id: Id; label: string; indicator: string | null }[];
    defaultOwnerUserId?: Id | null;
    /** AC7: the next free code inside this line ("C1.1" for line C1, "A2.3" after A2.2). */
    nextCode?: string;
  }[];
  budgetLines: BudgetLineRefV3[];
  /** Who can own an activity: the project team (editors only; empty otherwise). */
  owners?: { id: Id; name: string }[];
  /** AD3: the project's experts, for an (N)KE activity's mission (editors only). */
  experts?: { id: Id; name: string; category: string }[];
  /** The indicators a line's feedback question can count towards: shares from answers (team only). */
  shareIndicators?: { id: Id; label: string }[];
  nextCode: Record<Id, string>;
  /** R2-018: the report pages' Period choices (the reporting periods, then "Whole project"), the same list as GET /admin/periods. */
  periods: { value: string; label: string }[];
  /** The older period words a link may still carry (Year 1, Next 90 days, rp-2), so the filter can name them. */
  otherPeriods?: { value: string; label: string }[];
  /** D6: whether activities of this project need the funder's approval unless switched (service contract on, grant off). */
  approvalDefault: boolean;
  /** OB-F10: a grant. "Draft the ToR now" starts unticked there (a grant's activities rarely need a ToR up front). */
  grant?: boolean;
};
export const activityCode = z
  .string()
  .trim()
  .min(1, "Enter a code, for example A1.2.")
  .max(LIMITS.activityCodeV3)
  .regex(/^[A-Za-z0-9.\-_/]+$/, "Use letters, numbers, dots and dashes.");
export const newActivityBody = z.object({
  type: z.enum(ACTIVITY_TYPES),
  title: text(LIMITS.torTitle, "Title"),
  /** The main result (reports and the contract manager view use it). */
  resultId: uuid.nullable(),
  /**
   * W2: every result it serves, the main one first (resultId, when given,
   * is the main one). Left out: it serves only its main result.
   */
  resultIds: z.array(uuid).max(20, "Choose at most 20 results.").optional(),
  code: activityCode,
  startDate: isoDate.nullable(),
  endDate: isoDate.nullable(),
  location: textOrEmpty(LIMITS.location),
  /** Optional: a person without "See money" sends none (plan 2026-11-03). */
  budgetEstimateCents: centsV3().nullable().optional(),
  draftTorNow: z.boolean(),
  /** The line of work it belongs to (W1); its kind must be this type. */
  lineOfWorkId: uuid.nullable().optional(),
  /** Who owns it (Home per person): someone on the project team. Left out on create = the line's default owner, else the person creating it; null = nobody. */
  ownerUserId: uuid.nullable().optional(),
  /** W3: the budget lines it uses first. Left out on create = the line's. */
  budgetLineIds: z.array(uuid).max(100).optional(),
  /** W3: the proof it must produce. Left out on create = the line's. */
  requiredProof: z.array(z.enum(PROOF_KINDS)).max(PROOF_KINDS.length).optional(),
  /** W3 (events): which of the line's feedback questions its form starts with. Left out = all of them. */
  feedbackQuestionIds: z.array(uuid).max(20).optional(),
  /** D6 "Needs the funder's approval": true or false for this activity; null or left out = the project default. */
  needsApproval: z.boolean().nullable().optional(),
  /** AD3 ((N)KE only): the expert, their days and what the mission must produce. */
  mission: z
    .object({
      engagementId: uuid.nullable(),
      days: z.number().min(0, "Enter the days as 0 or more.").max(366, "At most 366 days.").multipleOf(0.5, "Use whole or half days.").nullable(),
      output: textOrEmpty(2_000, "Output"),
    })
    .optional(),
  /** AT4 (deliverables only): its type, authors and link. The planned publication date is endDate. */
  deliverable: z
    .object({
      kind: z.enum(DELIVERABLE_KINDS),
      authors: textOrEmpty(500, "Authors"),
      url: httpUrlOrEmpty(500),
    })
    .optional(),
});
export type NewActivityBody = z.infer<typeof newActivityBody>;
export const activityPatch = newActivityBody.omit({ type: true, draftTorNow: true, feedbackQuestionIds: true, mission: true, deliverable: true }).partial();
export type ActivityPatch = z.infer<typeof activityPatch>;
export type ActivityHeader = ActivityRow & {
  /** The main result. */
  result: ResultRefV3 | null;
  /** W2: every result it serves, the main one first. */
  results?: ResultRefV3[];
  /** The line of work (W1); event pages leave it out. */
  lineOfWork?: { id: Id; name: string } | null;
  location: string;
  tor: { id: Id; reference: string; status: StatusChip; version: number; daysWithFunder: number | null; approvedOn: string | null } | null;
  hold: { active: boolean; message: string } | null;
  imported: { fileName: string; fileUrl: string } | null;
  canEdit: boolean;
  /** Home per person: who owns it (null = nobody), and who could (the project team; only for editors). Event pages leave them out. */
  owner?: { id: Id; name: string } | null;
  ownerChoices?: { id: Id; name: string }[];
  /** W3: the proof it must produce (copied from its line when made) and whether each is on file. Partners and event pages may leave it out. */
  requiredProof?: { kind: ProofKind; label: string; onFile: boolean }[];
  /** W3: the budget lines it uses first (copied from its line when made). Team only. */
  budgetLines?: BudgetLineRefV3[];
  /**
   * D6 "Needs the funder's approval" (team only): needed = what applies now;
   * own = the activity's own choice (null = the project default);
   * projectDefault = the project's.
   */
  approval?: ActivityApproval;
};
export type ActivityApproval = { needed: boolean; own: boolean | null; projectDefault: boolean };
/** AT5 (round 5): "todo" = open to-dos and follow-ups; "deadline" = RSVP deadlines and the funder's ToR decisions. */
export type CalendarEntry = { id: string; date: string; label: string; category: "event" | "mission_due" | "meeting" | "report" | "todo" | "deadline"; path: string };
export type CalendarMonth = {
  month: string;
  prev: { month: string; label: string };
  next: { month: string; label: string };
  weeks: { date: string; inMonth: boolean; entries: CalendarEntry[] }[][];
};
export const calendarQuery = z.object({ month: isoMonth.optional() });
export type CalendarSubscription = { url: string };

// ---------- 7.3 meetings and steering committee ----------

export const meetingKind = z.enum(MEETING_KINDS);
export const logMeetingBody = z.object({
  date: isoDate,
  title: text(200, "Title"),
  kind: meetingKind,
  place: textOrEmpty(LIMITS.location),
  mode: z.enum(MEETING_MODES),
  contactIds: z.array(uuid).max(200),
  newContacts: z
    .array(z.object({ name: text(LIMITS.userName, "Name"), stakeholderOrgId: uuid.nullable(), newOrgName: textOrNull(LIMITS.stakeholderOrgName) }))
    .max(50),
  stakeholderOrgId: uuid.nullable(),
  newOrgName: textOrNull(LIMITS.stakeholderOrgName),
  discussion: textOrEmpty(20_000),
  nextSteps: textOrEmpty(10_000),
  activityId: uuid.nullable(),
  countAsExpertDays: z.boolean(),
  internal: z.boolean(),
  /** Round 5 (EV12): the result the meeting worked towards (when not about one activity). */
  resultId: uuid.nullable().optional(),
  /** Round 5 (EV12): the next steps, each with an owner on the team and a date; when given, they replace nextSteps. */
  steps: z
    .array(z.object({ action: text(1000, "Next step"), whoUserId: uuid.nullable(), dueDate: isoDate.nullable() }))
    .max(50)
    .optional(),
});
export type LogMeetingBody = z.infer<typeof logMeetingBody>;
export const meetingPatch = logMeetingBody.omit({ steps: true }).partial().extend({ status: z.enum(["planned", "notes_filed"]).optional() });
export type MeetingRow = {
  id: Id;
  date: string;
  title: string;
  subLine: string;
  kind: MeetingKind;
  with: string;
  linkedTo: string;
  notes: StatusChip;
  path: string;
};
/** X-21: a note on a person or organisation about an activity, shown with the activity's meetings. */
export type ActivityNoteRow = { id: Id; title: string; text: string; date: string; path: string };
export type MeetingDetail = {
  id: Id;
  title: string;
  kind: MeetingKind;
  status: StatusChip;
  subLine: string;
  date: string;
  place: string;
  mode: string;
  discussion: string;
  nextSteps: string;
  internal: boolean;
  actions: { id: Id; action: string; who: string; whoUserId: Id | null; whoContactId: Id | null; dueDate: string | null; done: boolean }[];
  present: { name: string; organisation: string | null; path: string | null }[];
  /** Who can be named in a next step (editors only; empty otherwise): the team, then the people present. */
  whoOptions: { kind: "user" | "contact"; id: Id; name: string }[];
  linked: Link[];
  attachments: FileRef[];
  history: HistoryEntry[];
  canEdit: boolean;
};
export const meetingActionsBody = z.object({
  actions: z
    .array(
      z.object({
        id: uuid.optional(),
        action: text(1000, "Action"),
        whoUserId: uuid.nullable(),
        whoContactId: uuid.nullable(),
        dueDate: isoDate.nullable(),
        done: z.boolean(),
      }),
    )
    .max(100),
});

export const newSteeringBody = z.object({ date: isoDate, place: textOrEmpty(LIMITS.location), chair: textOrEmpty(LIMITS.userName) });
export const paperSource = z.enum(PAPER_SOURCES);
export const agendaBody = z.object({
  items: z
    .array(
      z.object({
        id: uuid.optional(),
        item: text(500, "Agenda item"),
        paperSource,
        paperRefId: uuid.nullable(),
        paperFileId: uuid.nullable(),
      }),
    )
    .max(40),
});
export const scMembersBody = z.object({
  members: z.array(z.object({ contactId: uuid.nullable(), stakeholderOrgId: uuid.nullable(), label: text(200, "Member") })).max(60),
});
/** AD2: contactId / stakeholderOrgId = the stakeholder the member is (picked from stakeholders); emails = the extra addresses kept with it. */
export type ScMember = { id: Id; label: string; email: string | null; people: number; path: string | null; contactId?: Id | null; stakeholderOrgId?: Id | null; emails?: string[] };
export type ScDecision = { id: Id; decision: string; status: "open" | "in_progress" | "done"; fromMeeting: number };
export const scDecisionPatch = z.object({ status: z.enum(["open", "in_progress", "done"]) });
export type SteeringDetail = {
  id: Id;
  number: number;
  title: string;
  date: string;
  place: string;
  chair: string;
  papersDue: string;
  papersSentAt: string | null;
  minutesSentAt: string | null;
  agenda: { id: Id; position: number; item: string; paper: { label: string; source: PaperSource; path: string | null; file: FileRef | null } | null }[];
  previousDecisions: ScDecision[];
  members: ScMember[];
  files: FileRef[];
  canEdit: boolean;
};
export type PapersDraft = {
  due: string;
  /** previewPath (AD2): the generated paper, to read before sending (API path); cv: it holds CVs, so sending asks a confirmation. */
  papers: { id: string; label: string; checked: boolean; previewPath?: string | null; cv?: boolean }[];
  recipients: { id: Id; member: string; email: string }[];
  message: string;
  /** The papers emails sent so far, latest per address: queued, sent, trying again or failed (EM3). */
  mails: MailStatus[];
};
export const papersSendBody = z.object({
  paperIds: z.array(z.string().max(80)).max(40),
  recipientIds: z.array(uuid).min(1).max(200),
  message: textOrEmpty(10_000),
  /** AD2: the sender confirmed that the members may receive the CVs among the papers. */
  cvsConfirmed: z.boolean().optional(),
});
export const minutesBody = z.object({
  present: z.array(uuid).max(200),
  items: z.array(z.object({ agendaItemId: uuid, discussion: textOrEmpty(20_000), decision: textOrEmpty(5_000) })).max(40),
});
export type MinutesBody = z.infer<typeof minutesBody>;
/** opensOn (AD2): the meeting date while it is still ahead (minutes are written from then on); null once it has come. */
export type MinutesDraft = MinutesBody & { draftedFrom: string; members: ScMember[]; agenda: { id: Id; item: string }[]; opensOn?: string | null };

// ---------- 7.4 deliverables ----------

export const deliverablePatchV3 = z
  .object({
    authorEngagementId: uuid.nullable().optional(),
    authorUserId: uuid.nullable().optional(),
    dueDate: isoDate.optional(),
    sources: textOrEmpty(10_000).optional(),
    visibilityRequirement: textOrEmpty(2_000).optional(),
  })
  .strict();
export const deliverableVersionBody = z.object({ fileId: uuid, euStatement: z.boolean() });
export const requestChangesBody = z.object({ text: text(10_000, "Comment"), nextDraftDue: isoDate.nullable() });
/** Approve a draft in review (AT2: one step; publishing is the next one). The comment goes into Review comments. */
export const approveDeliverableBody = z.object({ notifyConsulted: z.boolean(), comment: textOrEmpty(10_000).optional() });
/** Publish an approved deliverable (AT2): the publication date and, when it is online elsewhere, its web address. */
export const publishDeliverableBody = z.object({ publishedOn: isoDate, url: httpUrlOrEmpty(500) });
/** A review comment on the current version (AT2). */
export const deliverableCommentBody = z.object({ text: text(10_000, "Comment") });
export type ApprovalCheck = {
  version: number;
  /** toCheck: not done, but approval may go ahead once someone has looked (DL-N1: no author days logged). */
  checks: { key: "eu_statement" | "author_days" | "sources"; label: string; ok: boolean; detail: string; toCheck?: boolean }[];
  consultedStakeholders: number;
};
export type DeliverableDetail = {
  header: ActivityHeader;
  status: "planned" | "drafting" | "in_review" | "approved" | "published";
  steps: Steps;
  author: { name: string; label: string; path: string | null } | null;
  files: { file: FileRef; version: number; uploadedAt: string; uploadedBy: string }[];
  comments: { author: string; version: number; text: string; kind: "comment" | "changes_requested" | "approved"; at: string }[];
  /** url: the counter link; publishedOn and link: as given when publishing (AT2). */
  publication: { url: string; views: number; downloads: number; publishedOn?: string | null; link?: string | null } | null;
  /** AT4: type and authors as the publication names them; link = the URL given so far. */
  kind?: DeliverableKind;
  authors?: string;
  link?: string | null;
  details: { result: string | null; budgetCents: number | null; daysUsed: number; daysPlanned: number; sources: string; visibility: string };
  nextDraftDue: string | null;
  history: HistoryEntry[];
  canReview: boolean;
  canUpload: boolean;
  /** The one next step and who may take it (AT2: one step at a time). */
  next?: { step: "upload" | "submit" | "review" | "publish"; allowed: boolean } | null;
};

// ---------- 7.5 missions and mission reports ----------

export const missionPatch = z
  .object({
    travel: textOrEmpty(2_000).optional(),
    travelBooked: z.boolean().optional(),
    contributesTo: z.array(z.object({ activityId: uuid, note: textOrEmpty(500) })).max(20).optional(),
    /** AD3: what the mission must produce. */
    output: textOrEmpty(2_000, "Output").optional(),
  })
  .strict();
export type MissionDetail = {
  header: ActivityHeader;
  expert: { name: string; category: string; path: string } | null;
  days: { value: number; note: string };
  /** AD3: what the mission must produce ("" = not said). */
  output?: string;
  budget: { feesCents: number; travelCents: number; perDiemCents: number };
  meetings: {
    planned: number;
    confirmed: number;
    rows: { id: Id; date: string; with: string; country: string | null; organisation: string | null; status: StatusChip; path: string }[];
  };
  contributesTo: { activity: Link; note: string }[];
  details: { result: string | null; tor: Link | null; travel: string; travelBooked: boolean };
  evidence: { fromRecord: FromRecord[]; reportPath: string };
  /** The documents attached to the mission (uploads, contracts, quotes, the report's attachments). Team only: null for partners. */
  documents: FileRef[] | null;
  canEdit: boolean;
};

// ---------- mission proposals (round 4, "missions as deep as events") ----------

export const MISSION_PROPOSAL_KINDS = ["per_diem", "flight", "timesheet_day"] as const;
export type MissionProposalKind = (typeof MISSION_PROPOSAL_KINDS)[number];
/** A budget line a per diem or a flight may go on. */
export type ProposalLine = { id: Id; name: string; code: string; unitLabel: string | null; unitValueCents: number | null };
/**
 * Something the mission knows and proposes; nothing happens until someone
 * answers. Per diems: an expense the expert claims, to approve. Flights: an
 * order (committed until its invoice comes). Timesheet days: a draft line on
 * the expert's timesheet, which the expert still signs.
 */
export type MissionProposal = {
  kind: MissionProposalKind;
  key: string;
  expertName: string;
  /** "Per diems for Jean Morel: 4 days at €240 (€960)?" (nights on a line counted in nights) */
  question: string;
  /** Where the numbers come from. */
  detail: string;
  units: number;
  /** Per diems and flights: the budget lines it may go on; one chosen when only one fits. */
  lines: ProposalLine[];
  budgetLineId: Id | null;
  /** The rate from the chosen line's unit value; null when the line has none. */
  unitValueCents: number | null;
  amountCents: number | null;
  /** Why it cannot be added now (the ToR is not approved, the month is signed, the line has no rate). */
  blockedReason: string | null;
};
export type MissionProposals = {
  proposals: MissionProposal[];
  /** Plain notes: "No budget line for flights: log them by hand." */
  notes: string[];
};
export const missionProposalAnswer = z.object({
  kind: z.enum(MISSION_PROPOSAL_KINDS),
  key: z.string().min(1).max(120),
  answer: z.enum(["add", "not_needed"]),
  /** Per diems and flights: the line, when the proposal did not choose one (or to choose another). */
  budgetLineId: uuid.nullable().optional(),
  /** Per diems and flights: how many, when fewer than proposed (never more). */
  units: z.number().positive().max(366).optional(),
});
export type MissionProposalAnswer = z.input<typeof missionProposalAnswer>;
/**
 * A person met on the mission: a name, a short note, and (staff only) the
 * stakeholder it is. `id` keeps a row that already exists, so a stakeholder
 * staff linked stays linked when the expert saves again.
 */
export const missionPersonItem = z.object({
  id: uuid.optional(),
  contactId: uuid.nullable().optional(),
  name: text(120, "Name"),
  note: textOrEmpty(2000),
});
export const missionReportBody = z.object({
  findings: textOrEmpty(50_000),
  recommendations: textOrEmpty(50_000),
  feedsActivityId: uuid.nullable(),
  attachmentFileIds: z.array(uuid).max(30),
  /** Left out = the list stays as it is. */
  peopleMet: z.array(missionPersonItem).max(100).optional(),
});
/** PUT /p/:projectId/missions/:activityId/report/people: staff link the names to stakeholders, also after submitting. */
export const missionPeopleBody = z.object({ peopleMet: z.array(missionPersonItem).max(100) });
/** One person met on the mission. For the expert (portal), `contact` is always null and only `linked` says a stakeholder was picked. */
export type MissionPersonMet = {
  id: Id;
  name: string;
  note: string;
  linked: boolean;
  /** organisation: the stakeholder's organisation (staff only; round 4 mission page). */
  contact: { id: Id; name: string; path: string; organisation?: string | null } | null;
};
export type MissionReport = {
  activityId: Id;
  status: "draft" | "submitted";
  fromRecord: string;
  findings: string;
  recommendations: string;
  feedsActivity: { id: Id; code: string; title: string } | null;
  attachments: FileRef[];
  submittedAt: string | null;
  submittedBy: string | null;
  feedOptions: { id: Id; code: string; title: string }[];
  peopleMet: MissionPersonMet[];
  /** Staff can pick stakeholders; the expert only writes names. */
  canLinkPeople: boolean;
};

// ---------- additions by WP activities (optional fields and helper shapes, additive only) ----------

/** CalendarMonth.label: "October 2026" (added; optional). */
export type CalendarMonthWithLabel = CalendarMonth & { label?: string };
/** GET /p/:projectId/meetings/new-options?activityId= (Log a meeting from a mission). */
export type MeetingNewOptions = {
  activity: { id: Id; code: string; title: string; type: ActivityType } | null;
  expert: { name: string } | null;
  /** Round 5 (EV12): what the meeting can be linked to, and who can own a next step (empty for partners). */
  /** resultId (EV-N7): the activity's result, the meeting's default result. */
  activities?: { id: Id; code: string; title: string; resultId?: Id | null }[];
  results?: { id: Id; code: string; label: string }[];
  team?: { id: Id; name: string }[];
};
/** GET /p/:projectId/meetings/lookup/people|organisations?q= (type-ahead). */
export type LookupItems = { items: { id: Id; label: string; sub?: string }[] };
/** PUT /p/:projectId/steering/members: each member may carry a group of email addresses (added; optional). */
export const scMembersBodyV3 = z.object({
  members: z
    .array(
      z.object({
        contactId: uuid.nullable(),
        stakeholderOrgId: uuid.nullable(),
        label: text(200, "Member"),
        emails: z.array(z.string().trim().toLowerCase().email()).max(100).optional(),
      }),
    )
    .max(60),
});
/** The portal's mission report adds the activity code and title. */
export type PortalMissionReport = MissionReport & { activity: { code: string; title: string; projectId: Id } };

// ---------- to-dos with an owner (Logistics tab; later Home, missions, meetings) ----------

/** One to-do. `activity` is null for a to-do on the project as a whole. */
export type TodoRow = {
  id: Id;
  title: string;
  activity: { id: Id; code: string; title: string; type: ActivityType; path: string } | null;
  /** A team member of the project; null = not assigned. */
  owner: { id: Id; name: string } | null;
  /** The stakeholder it is about (shows on their record and as their next step); null = nobody in particular. */
  about: { id: Id; name: string; path: string } | null;
  dueOn: string | null;
  /** E14: when in the event's life it happens (TODO_PHASE_LABELS); null = no phase. */
  phase: TodoPhase | null;
  /** "Due 7 Oct", "Overdue: due 3 Oct", "Done 2 Oct by Ana Petrova"; null when open without a date. */
  dueLabel: string | null;
  overdue: boolean;
  done: boolean;
  doneAt: string | null;
  doneBy: string | null;
  /** The signed-in person owns it. */
  mine: boolean;
  canEdit: boolean;
};
/** GET /p/:projectId/todos and /p/:projectId/activities/:activityId/todos: open first (by due date), then done. */
export type TodoList = {
  rows: TodoRow[];
  /** Who can own a to-do: the project team (owners, admins, members; not viewers). */
  owners: { id: Id; name: string }[];
  /** The signed-in person (the add form offers them as owner first). */
  youUserId: Id;
  canEdit: boolean;
  openCount: number;
  mineOpenCount: number;
  /**
   * People offered first in "About" (a mission: the people met; an event: the
   * people on its list); anyone else is found with the stakeholder search.
   * Null on the project's own list.
   */
  aboutSuggestions: { label: string; people: { id: Id; name: string }[] } | null;
  /** Today in Brussels (YYYY-MM-DD), for the "When" groups (Overdue, This week, Later, No date). */
  today: string;
  /**
   * E14: the list of an event: to-dos have a phase, and "Add the standard event checklist"
   * adds `checklistMissing` items (those not on the list yet, by title). Null elsewhere.
   */
  event: { checklistMissing: number } | null;
};
/** GET /me/todos: my open to-dos across the projects of my organisation, by due date (no date last). */
export type MyTodo = TodoRow & { project: { id: Id; name: string; reference: string } };
export type MyTodos = { rows: MyTodo[] };

export const todoBody = z.object({
  title: text(500, "To-do"),
  ownerUserId: uuid.nullable().default(null),
  dueOn: isoDate.nullable().default(null),
  /** The activity it belongs to; null = the project as a whole. */
  activityId: uuid.nullable().default(null),
  /** The stakeholder it is about; null = nobody in particular. */
  contactId: uuid.nullable().default(null),
  /** E14: when in the event's life it happens; null = no phase. */
  phase: z.enum(TODO_PHASES).nullable().default(null),
});
export type TodoBody = z.input<typeof todoBody>;
export const todoPatch = z.object({ title: text(500, "To-do"), ownerUserId: uuid.nullable(), dueOn: isoDate.nullable(), contactId: uuid.nullable(), phase: z.enum(TODO_PHASES).nullable() }).partial();
/** POST …/activities/:activityId/todos/standard-checklist: how many to-dos were added. */
export type StandardChecklistResult = { added: number };
export type TodoPatch = z.infer<typeof todoPatch>;
export const todosQuery = z.object({
  activityId: uuid.optional(),
  /** "1": only mine. */
  mine: z.enum(["0", "1"]).optional(),
  /** "1": only open ones. */
  open: z.enum(["0", "1"]).optional(),
});
